// GltfKindStep — "what kind of glTF is this?", asked on every .glb / .gltf drop.
//
// Unreal's FBX import dialog is the model: the importer reads the file, fills
// the settings in with what it found, and the person confirms or changes them.
// It never files the asset silently, and it never makes you answer from
// scratch.
//
// What this replaces: a content sniff that DECIDED. It was right on every file
// measured and still wrong as a design, because its garment rule is "unskinned
// meshes with geometry" — which is every unskinned mesh in the world. A
// confident wrong answer nobody is shown is worse than a question.
//
// Three UX rules it follows, each of which has cost a bug in this repo before:
//   - The suggestion is QUOTED, not just applied. The summary line says what
//     was measured ("36 unskinned meshes with geometry, no animation"), so a
//     wrong guess can be argued with rather than just overridden blind.
//   - An unreadable file pre-selects NOTHING and the confirm button is
//     disabled. A dialog that arrives with a guess already chosen for a file it
//     could not read gets imported by anyone pressing Enter.
//   - The destructive option (replacing the rig) is shown, selectable, and
//     WARNED about in place. Hiding it would leave the person who genuinely
//     dropped a rig with no way through and nothing explaining why.

import React, { useState } from 'react'
import WizardShell, { tokens } from './WizardShell'
import { gltfImportChoice } from '../../../lib/garment/gltfImportChoice'

function fmtSize(bytes) {
  if (!bytes) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let n = Number(bytes), i = 0
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${units[i]}`
}

export default function GltfKindStep({ file, detected, onChoose, onCancel }) {
  const choice = gltfImportChoice(detected)
  const [picked, setPicked] = useState(choice.suggested ?? '')
  const option = choice.options.find(o => o.wizardKey === picked) ?? null

  const size = fmtSize(file?.size)

  return (
    <WizardShell
      title="Import glTF"
      subtitle={size ? `${file?.name} \u00b7 ${size}` : file?.name}
      onCancel={onCancel}
      onSubmit={() => { if (picked) onChoose?.(picked) }}
      submitDisabled={!picked}
      submitLabel={option ? `Import as ${option.label.replace(/ \(.*\)$/, '')}` : 'Import'}
    >
      {/* What the bytes look like. Shown ABOVE the control it pre-filled, so it
          reads as the reason for the selection rather than as a footnote. */}
      <div
        data-testid="gltf-kind-detected"
        style={{
          background: tokens.bgInset, border: `1px solid ${tokens.border}`,
          borderRadius: 4, padding: '8px 10px', marginBottom: 14,
          color: tokens.dim, fontFamily: 'var(--m-font)', fontSize: 11, lineHeight: 1.5,
        }}
      >
        {choice.summary}
      </div>

      <label
        htmlFor="gltf-kind-select"
        style={{
          display: 'block', color: tokens.faint, fontFamily: 'var(--m-font)',
          fontSize: 9, letterSpacing: '.08em', marginBottom: 5,
        }}
      >
        IMPORT AS
      </label>
      <select
        id="gltf-kind-select"
        data-testid="gltf-kind-select"
        value={picked}
        onChange={e => setPicked(e.target.value)}
        style={{
          width: '100%', background: tokens.bgInset,
          border: `1px solid ${tokens.border}`, borderRadius: 4,
          color: tokens.text, fontFamily: 'var(--m-font)', fontSize: 12,
          padding: '7px 8px', outline: 'none', boxSizing: 'border-box',
        }}
      >
        {/* Present only while nothing is chosen: once a real type is picked,
            offering "choose…" back would be offering a state the dialog cannot
            act on. */}
        {!choice.suggested && <option value="">{'— choose a type —'}</option>}
        {choice.options.map(o => (
          <option key={o.wizardKey} value={o.wizardKey}>{o.label}</option>
        ))}
      </select>

      {option && (
        <div style={{
          marginTop: 8, color: tokens.dim, fontFamily: 'var(--m-font)',
          fontSize: 10, lineHeight: 1.6,
        }}>
          {option.hint}
        </div>
      )}

      {option?.destructive && (
        <div
          data-testid="gltf-kind-destructive"
          style={{
            marginTop: 12, padding: '8px 10px', borderRadius: 4,
            background: 'color-mix(in srgb, var(--m-warn) 12%, var(--m-bg))',
            border: `1px solid color-mix(in srgb, var(--m-warn) 45%, transparent)`,
            color: tokens.warn, fontFamily: 'var(--m-font)', fontSize: 10, lineHeight: 1.6,
          }}
        >
          This replaces the rig the character is built on. You will be asked to
          confirm again before anything is uploaded.
        </div>
      )}
    </WizardShell>
  )
}
